import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { injectSchema, getBreadcrumbSchema, getWebPageSchema } from '../../utils/seo';

export default function Processus() {
  useEffect(() => {
    // Inject Schema.org structured data
    const howToSchema = {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: 'Processus de nettoyage professionnel Spark\'Up',
      description: 'Méthode professionnelle en 7 étapes pour un nettoyage en profondeur de vos canapés et tissus',
      step: [
        {
          '@type': 'HowToStep',
          position: 1,
          name: 'Prise de contact',
          text: 'Contactez-nous par téléphone, email ou via notre formulaire en ligne'
        },
        {
          '@type': 'HowToStep',
          position: 2,
          name: 'Devis gratuit',
          text: 'Nous établissons un devis détaillé et transparent, sans engagement'
        },
        {
          '@type': 'HowToStep',
          position: 3,
          name: 'Intervention à domicile',
          text: 'Notre équipe se déplace chez vous avec tout le matériel professionnel'
        },
        {
          '@type': 'HowToStep',
          position: 4,
          name: 'Inspection et diagnostic',
          text: 'Analyse approfondie de l\'état de vos tissus et identification des taches'
        },
        {
          '@type': 'HowToStep',
          position: 5,
          name: 'Nettoyage professionnel',
          text: 'Application de nos techniques professionnelles avec produits adaptés'
        },
        {
          '@type': 'HowToStep',
          position: 6,
          name: 'Séchage et finitions',
          text: 'Séchage rapide et application de traitements protecteurs si nécessaire'
        },
        {
          '@type': 'HowToStep',
          position: 7,
          name: 'Résultat et satisfaction',
          text: 'Vérification du résultat ensemble et conseils d\'entretien personnalisés'
        }
      ]
    };

    const schemas = [
      getWebPageSchema(
        'Notre Processus - Spark\'Up',
        'Méthode professionnelle en 7 étapes pour un nettoyage en profondeur de vos canapés et tissus'
      ),
      howToSchema,
      getBreadcrumbSchema([
        { name: 'Accueil', url: '/' },
        { name: 'Processus', url: '/processus' }
      ])
    ];
    
    injectSchema(schemas);
  }, []);

  const steps = [
    {
      number: 1,
      title: "Prise de contact",
      description: "Contactez-nous par téléphone, email ou via notre formulaire en ligne",
      icon: "ri-phone-line",
      image: "https://public.readdy.ai/ai/img_res/e616b2b9338c0d9d41de4d5d7030ec7f.jpg",
      details: [
        "Réponse rapide sous 2h en semaine",
        "Écoute de vos besoins spécifiques",
        "Conseils personnalisés gratuits",
        "Prise de rendez-vous flexible"
      ]
    },
    {
      number: 2,
      title: "Devis gratuit",
      description: "Nous établissons un devis détaillé et transparent, sans engagement",
      icon: "ri-file-text-line",
      image: "https://readdy.ai/api/search-image?query=professional%20cleaning%20service%20estimate%20document%20on%20tablet%20screen%20with%20calculator%20and%20pen%20on%20modern%20desk%20transparent%20pricing%20detailed%20quote%20bright%20office%20setting%20high%20quality%20photography%20realistic%20simple%20clean%20background&width=600&height=400&seq=process2&orientation=landscape",
      details: [
        "Tarification claire et détaillée",
        "Aucun frais caché",
        "Devis envoyé sous 24h",
        "Validité de 30 jours"
      ]
    },
    {
      number: 3,
      title: "Intervention à domicile",
      description: "Notre équipe se déplace chez vous avec tout le matériel professionnel",
      icon: "ri-home-line",
      image: "https://readdy.ai/api/search-image?query=professional%20cleaning%20technician%20in%20uniform%20arriving%20at%20home%20with%20industrial%20cleaning%20equipment%20van%20in%20background%20carrying%20professional%20steam%20cleaner%20friendly%20service%20worker%20high%20quality%20photography%20realistic%20detailed%20simple%20clean%20background&width=600&height=400&seq=process3&orientation=landscape",
      details: [
        "Ponctualité garantie",
        "Protection de votre intérieur",
        "Équipement professionnel complet",
        "Respect de votre domicile"
      ]
    },
    {
      number: 4,
      title: "Inspection et diagnostic",
      description: "Analyse approfondie de l'état de vos tissus et identification des taches",
      icon: "ri-search-eye-line",
      image: "https://readdy.ai/api/search-image?query=professional%20upholstery%20cleaner%20inspecting%20fabric%20sofa%20closely%20examining%20stains%20and%20material%20with%20magnifying%20tool%20diagnostic%20assessment%20in%20bright%20living%20room%20high%20quality%20photography%20realistic%20detailed%20simple%20clean%20background&width=600&height=400&seq=process4&orientation=landscape",
      details: [
        "Identification du type de tissu",
        "Repérage des zones problématiques",
        "Test de compatibilité des produits",
        "Explication du processus"
      ]
    },
    {
      number: 5,
      title: "Nettoyage professionnel",
      description: "Application de nos techniques professionnelles avec produits adaptés",
      icon: "ri-brush-line",
      image: "https://readdy.ai/api/search-image?query=professional%20cleaning%20technician%20using%20industrial%20steam%20cleaning%20machine%20on%20fabric%20sofa%20deep%20cleaning%20process%20in%20action%20with%20professional%20equipment%20modern%20living%20room%20high%20quality%20photography%20realistic%20detailed%20showing%20cleaning%20technique%20simple%20clean%20background&width=600&height=400&seq=process5&orientation=landscape",
      details: [
        "Shampouineuse professionnelle",
        "Produits écologiques certifiés",
        "Traitement des taches tenaces",
        "Désinfection en profondeur"
      ]
    },
    {
      number: 6,
      title: "Séchage et finitions",
      description: "Séchage rapide et application de traitements protecteurs si nécessaire",
      icon: "ri-sun-line",
      image: "https://readdy.ai/api/search-image?query=clean%20fresh%20sofa%20drying%20with%20professional%20air%20blower%20equipment%20final%20touches%20being%20applied%20protective%20treatment%20spray%20bottle%20nearby%20bright%20living%20room%20high%20quality%20photography%20realistic%20detailed%20simple%20clean%20background&width=600&height=400&seq=process6&orientation=landscape",
      details: [
        "Séchage accéléré professionnel",
        "Traitement anti-taches optionnel",
        "Brossage des fibres",
        "Vérification finale qualité"
      ]
    },
    {
      number: 7,
      title: "Résultat et satisfaction",
      description: "Vérification du résultat ensemble et conseils d'entretien personnalisés",
      icon: "ri-checkbox-circle-line",
      image: "https://readdy.ai/api/search-image?query=satisfied%20customer%20and%20professional%20cleaner%20standing%20next%20to%20perfectly%20cleaned%20pristine%20sofa%20both%20smiling%20happy%20with%20results%20bright%20modern%20living%20room%20high%20quality%20photography%20realistic%20detailed%20showing%20satisfaction%20simple%20clean%20background&width=600&height=400&seq=process7&orientation=landscape",
      details: [
        "Contrôle qualité avec vous",
        "Conseils d'entretien gratuits",
        "Garantie satisfaction",
        "Suivi après intervention"
      ]
    }
  ];

  const equipment = [
    {
      icon: "ri-tools-fill",
      title: "Shampouineuse professionnelle",
      description: "Équipement à injection-extraction de dernière génération"
    },
    {
      icon: "ri-flask-line",
      title: "Produits certifiés",
      description: "Solutions écologiques adaptées à chaque type de tissu"
    },
    {
      icon: "ri-shield-check-line",
      title: "Matériel haute performance",
      description: "Aspirateurs industriels et brosses professionnelles"
    },
    {
      icon: "ri-leaf-line",
      title: "Approche écologique",
      description: "Respect de l'environnement et de votre santé"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/39f01b0f1c683637cd8f53dc045bcf45/b7bb771b8d681dae1702525950b7e60d.png" 
                alt="Spark'Up Logo" 
                className="h-16 w-auto"
              />
            </Link>
            <div className="hidden md:flex space-x-10">
              <Link to="/" className="text-gray-600 hover:text-navy font-medium transition-colors whitespace-nowrap cursor-pointer">
                Accueil
              </Link>
              <Link to="/services" className="text-gray-600 hover:text-navy font-medium transition-colors whitespace-nowrap cursor-pointer">
                Services
              </Link>
              <Link to="/processus" className="text-navy font-medium whitespace-nowrap cursor-pointer">
                Processus
              </Link>
              <Link to="/a-propos" className="text-gray-600 hover:text-navy font-medium transition-colors whitespace-nowrap cursor-pointer">
                À propos
              </Link>
              <Link to="/contact" className="text-gray-600 hover:text-navy font-medium transition-colors whitespace-nowrap cursor-pointer">
                Contact
              </Link>
            </div>
            <Link to="/contact" className="bg-navy text-white px-8 py-3 rounded-full font-semibold hover:bg-navy-dark transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer">
              Devis gratuit
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-premium-gray via-white to-sky/5 py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-sky font-semibold text-sm tracking-wider uppercase mb-4 block">Méthode</span>
          <h2 className="text-5xl lg:text-7xl font-bold text-navy mb-6">Notre Processus</h2>
          <p className="text-xl lg:text-2xl text-gray-600 font-light max-w-3xl mx-auto">
            Une méthode professionnelle en 7 étapes pour des résultats impeccables
          </p>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {steps.map((step, index) => (
              <div key={index} className={`flex flex-col lg:flex-row gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-sky to-navy text-white rounded-2xl font-bold text-2xl flex-shrink-0">
                      {step.number}
                    </div>
                    <h4 className="text-3xl font-bold text-navy">{step.title}</h4>
                  </div>
                  <p className="text-xl text-gray-600 mb-8 leading-relaxed font-light">
                    {step.description}
                  </p>
                  <ul className="space-y-4">
                    {step.details.map((detail, idx) => (
                      <li key={idx} className="flex items-start gap-4">
                        <i className="ri-checkbox-circle-fill text-sky text-2xl flex-shrink-0 mt-1"></i>
                        <span className="text-gray-600 text-lg font-light">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex-1">
                  <div className="relative">
                    <div className="absolute -inset-4 bg-gradient-to-r from-sky/20 to-navy/20 rounded-3xl blur-2xl"></div>
                    <img 
                      src={step.image}
                      alt={step.title}
                      className="relative w-full h-80 object-cover object-top rounded-3xl shadow-2xl"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Equipment Section */}
      <section className="py-24 bg-premium-gray">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <span className="text-sky font-semibold text-sm tracking-wider uppercase mb-4 block">Équipement</span>
            <h3 className="text-4xl lg:text-5xl font-bold text-navy mb-6">
              Notre Équipement Professionnel
            </h3>
            <p className="text-xl text-gray-600 font-light">
              Du matériel de pointe pour des résultats optimaux
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {equipment.map((item, index) => (
              <div key={index} className="bg-white p-10 rounded-3xl shadow-md hover:shadow-xl transition-all text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-sky to-navy text-white rounded-2xl mx-auto mb-6">
                  <i className={`${item.icon} text-3xl`}></i>
                </div>
                <h4 className="text-xl font-bold text-navy mb-3">
                  {item.title}
                </h4>
                <p className="text-gray-600 font-light leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety & Quality */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sky font-semibold text-sm tracking-wider uppercase mb-4 block">Garantie</span>
              <h3 className="text-4xl lg:text-5xl font-bold text-navy mb-6">
                Sécurité et Efficacité Garanties
              </h3>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed font-light">
                Notre processus a été développé pour garantir la sécurité de vos meubles tout en assurant un nettoyage en profondeur. Nous utilisons uniquement des produits certifiés et adaptés à chaque type de matériau.
              </p>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-sky/20 to-navy/20 text-sky rounded-2xl flex-shrink-0">
                    <i className="ri-shield-check-line text-3xl"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-navy mb-2">Produits certifiés</h4>
                    <p className="text-gray-600 font-light">Solutions professionnelles testées et approuvées pour tous types de tissus</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-sky/20 to-navy/20 text-sky rounded-2xl flex-shrink-0">
                    <i className="ri-user-star-line text-3xl"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-navy mb-2">Équipe formée</h4>
                    <p className="text-gray-600 font-light">Techniciens certifiés et expérimentés dans le nettoyage textile</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-sky/20 to-navy/20 text-sky rounded-2xl flex-shrink-0">
                    <i className="ri-heart-pulse-line text-3xl"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-navy mb-2">Respect de votre santé</h4>
                    <p className="text-gray-600 font-light">Produits non toxiques, sans danger pour vous et vos animaux</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-sky/20 to-navy/20 rounded-3xl blur-2xl"></div>
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20cleaning%20technician%20in%20uniform%20using%20modern%20industrial%20upholstery%20cleaning%20equipment%20shampooer%20machine%20on%20elegant%20sofa%20in%20bright%20living%20room%20showing%20professional%20service%20quality%20detailed%20realistic%20photography%20simple%20clean%20background&width=800&height=600&seq=process8&orientation=landscape"
                  alt="Équipement professionnel de nettoyage"
                  className="relative rounded-3xl shadow-2xl w-full h-auto object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-navy to-navy-dark text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-4xl lg:text-5xl font-bold mb-6">
            Confiez-nous vos meubles en toute sérénité
          </h3>
          <p className="text-xl text-white/80 mb-10 font-light">
            Un processus éprouvé, des résultats garantis
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-navy px-10 py-4 rounded-full font-semibold text-lg hover:bg-premium-gray transition-all shadow-xl whitespace-nowrap cursor-pointer">
              Demander un devis gratuit
            </Link>
            <Link to="/services" className="bg-sky text-white px-10 py-4 rounded-full font-semibold text-lg hover:bg-sky/90 transition-all shadow-xl whitespace-nowrap cursor-pointer">
              Voir nos services
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12 mb-16">
            <div>
              <img 
                src="https://static.readdy.ai/image/39f01b0f1c683637cd8f53dc045bcf45/b7bb771b8d681dae1702525950b7e60d.png" 
                alt="Spark'Up Logo" 
                className="h-12 w-auto mb-6"
              />
              <p className="text-white/70 mb-6 font-light leading-relaxed">
                Nettoyage professionnel de canapés et tissus à Lausanne
              </p>
              <div className="flex gap-4">
                <a href="https://share.google/v4Ro6xy2LeuVbtJiq" target="_blank" rel="noopener noreferrer" className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-full hover:bg-sky transition-all cursor-pointer">
                  <i className="ri-google-fill text-xl"></i>
                </a>
                <a href="https://www.instagram.com/sparkup.swiss/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-full hover:bg-sky transition-all cursor-pointer">
                  <i className="ri-instagram-fill text-xl"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-6">Navigation</h4>
              <ul className="space-y-3">
                <li><Link to="/" className="text-white/70 hover:text-white transition-colors cursor-pointer font-light">Accueil</Link></li>
                <li><Link to="/services" className="text-white/70 hover:text-white transition-colors cursor-pointer font-light">Services</Link></li>
                <li><Link to="/processus" className="text-white/70 hover:text-white transition-colors cursor-pointer font-light">Processus</Link></li>
                <li><Link to="/a-propos" className="text-white/70 hover:text-white transition-colors cursor-pointer font-light">À propos</Link></li>
                <li><Link to="/contact" className="text-white/70 hover:text-white transition-colors cursor-pointer font-light">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-6">Services</h4>
              <ul className="space-y-3">
                <li className="text-white/70 font-light">Nettoyage de canapés</li>
                <li className="text-white/70 font-light">Nettoyage de fauteuils</li>
                <li className="text-white/70 font-light">Nettoyage de matelas</li>
                <li className="text-white/70 font-light">Nettoyage de cuir</li>
                <li className="text-white/70 font-light">Nettoyage de tapis</li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-6">Contact</h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-sky text-xl flex-shrink-0"></i>
                  <span className="text-white/70 font-light">Lausanne, Canton de Vaud, Suisse</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-phone-line text-sky text-xl"></i>
                  <a href="tel:+41782036539" className="text-white/70 hover:text-white transition-colors cursor-pointer font-light">+41 78 203 65 39</a>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-mail-line text-sky text-xl"></i>
                  <a href="mailto:Sparkup.swiss@gmail.com" className="text-white/70 hover:text-white transition-colors cursor-pointer font-light">Sparkup.swiss@gmail.com</a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center text-white/60 font-light">
            <p>&copy; 2025 Spark'Up. Tous droits réservés. | <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
